// apps/web/src/components/SheetShell.tsx
// Responsive, accessible dialog: a bottom-sheet on phones, a centered dialog on
// larger screens. Centralizes Escape-to-close, body scroll-lock, focus trap, and
// focus restore so every modal that renders through it inherits them.
import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTopmostEscape } from "../lib/useTopmostEscape";
import { useT } from "../lib/i18n";
import { useEdgeSwipe } from "../lib/useEdgeSwipe";

interface Props {
  onClose: () => void;
  children: ReactNode;
  /** Extra classes for the panel (e.g. a custom max-width). */
  className?: string;
  /** "sheet" (default) slides from the bottom on phones; "center" is a
   *  dialog in the middle of the screen on every size — for small settings
   *  forms (password, language, name), which read as popups, not sheets
   *  (owner decision). "full" is an iOS-style full-screen cover — the meal
   *  detail (scan result / logged meal) uses it so the view reads as its own
   *  screen, not a popup (owner). Drag-down and edge-swipe dismissal stay. */
  variant?: "sheet" | "center" | "full";
}

export function SheetShell({ onClose, children, className = "", variant = "sheet" }: Props) {
  const t = useT();
  const panelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  // Escape closes only the topmost modal (handles nested sheets safely).
  useTopmostEscape(onClose);

  // Left-edge swipe closes the sheet (iOS "go back" gesture) — the panel
  // follows the finger. Each sheet portal owns its own touches, so with
  // stacked sheets only the topmost one reacts.
  useEdgeSwipe(containerRef, { onCommit: onClose, targetRef: panelRef, backdropRef });

  // Drag-DOWN to dismiss (owner: "I swiped it down and it didn't disappear",
  // then: "make it fluent"). Engages only when the panel is scrolled to its
  // top and the finger moves downward, so it never fights inner scrolling.
  // The panel follows the finger 1:1 while the scrim thins with progress;
  // release past 110px OR a downward flick commits — and commits ANIMATE the
  // panel off-screen at finger speed before onClose fires, instead of
  // vanishing it from mid-air. Cancels spring home on Apple's curve.
  useEffect(() => {
    if (variant === "center") return;
    const panel = panelRef.current;
    if (!panel) return;
    const APPLE_EASE = "cubic-bezier(0.32, 0.72, 0, 1)";
    let startY = 0, engaged = false, tracking = false, dy = 0;
    let lastY = 0, lastT = 0, velo = 0, leaving = false;
    const scrim = () => backdropRef.current;
    const onStart = (e: TouchEvent) => {
      if (leaving || e.touches.length !== 1) return;
      tracking = panel.scrollTop <= 0;
      engaged = false;
      dy = 0;
      velo = 0;
      startY = lastY = e.touches[0].clientY;
      lastT = e.timeStamp;
    };
    const onMove = (e: TouchEvent) => {
      if (!tracking) return;
      const y = e.touches[0].clientY;
      dy = y - startY;
      if (!engaged) {
        if (dy > 10 && panel.scrollTop <= 0) engaged = true;
        else if (dy < -6) { tracking = false; return; }
        else return;
      }
      e.preventDefault();
      panel.style.transition = "none";
      panel.style.transform = `translateY(${Math.max(0, dy)}px)`;
      const bd = scrim();
      if (bd) {
        bd.style.transition = "none";
        bd.style.opacity = String(1 - Math.min(1, Math.max(0, dy) / panel.offsetHeight));
      }
      const dt = e.timeStamp - lastT;
      if (dt > 0) velo = 0.8 * ((y - lastY) / dt) + 0.2 * velo;
      lastY = y;
      lastT = e.timeStamp;
    };
    const settle = () => {
      const dur = Math.min(300, Math.max(180, dy / 0.9));
      panel.style.transition = `transform ${dur}ms ${APPLE_EASE}`;
      panel.style.transform = "";
      const bd = scrim();
      if (bd) { bd.style.transition = `opacity ${dur}ms ${APPLE_EASE}`; bd.style.opacity = ""; }
      setTimeout(() => { panel.style.transition = ""; if (bd) bd.style.transition = ""; }, dur + 40);
    };
    const leave = () => {
      leaving = true;
      const remaining = panel.offsetHeight - dy;
      const dur = Math.min(320, Math.max(140, remaining / Math.max(velo, 0.9)));
      panel.style.transition = `transform ${dur}ms ${APPLE_EASE}`;
      panel.style.transform = `translateY(${panel.offsetHeight + 40}px)`;
      const bd = scrim();
      if (bd) { bd.style.transition = `opacity ${dur}ms ${APPLE_EASE}`; bd.style.opacity = "0"; }
      let fired = false;
      const done = () => {
        if (fired) return;
        fired = true;
        panel.removeEventListener("transitionend", done);
        onClose();
      };
      panel.addEventListener("transitionend", done);
      setTimeout(done, dur + 80); // reduced-motion / display-change safety net
    };
    const onEnd = () => {
      if (!engaged) { tracking = false; return; }
      tracking = false; engaged = false;
      if (dy > 110 || (velo > 0.45 && dy > 24)) leave();
      else settle();
    };
    panel.addEventListener("touchstart", onStart, { passive: true });
    panel.addEventListener("touchmove", onMove, { passive: false });
    panel.addEventListener("touchend", onEnd);
    panel.addEventListener("touchcancel", onEnd);
    return () => {
      panel.removeEventListener("touchstart", onStart);
      panel.removeEventListener("touchmove", onMove);
      panel.removeEventListener("touchend", onEnd);
      panel.removeEventListener("touchcancel", onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  useEffect(() => {
    const prevActive = document.activeElement as HTMLElement | null;
    // The app's scroller is <main>, not the document (index.css pins html and
    // body). Locking body here would no longer stop the page behind the sheet
    // from scrolling under the user's finger.
    const scroller = document.querySelector("main");
    const prevOverflow = scroller?.style.overflow ?? "";
    if (scroller) scroller.style.overflow = "hidden";

    const panel = panelRef.current;
    const focusables = (): HTMLElement[] =>
      panel
        ? Array.from(
            panel.querySelectorAll<HTMLElement>(
              'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])',
            ),
          ).filter((el) => el.offsetParent !== null)
        : [];

    (focusables()[0] ?? panel)?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key !== "Tab") return;
      const f = focusables();
      if (f.length === 0) { e.preventDefault(); panel?.focus(); return; }
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      if (scroller) scroller.style.overflow = prevOverflow;
      prevActive?.focus?.();
    };
  }, []);

  // Portal to <body> so `position: fixed` is relative to the viewport, not to a
  // transformed page ancestor (the page roots use animate-rise, whose lingering
  // transform would otherwise anchor the sheet to the scrolled page bottom).
  return createPortal(
    <div
      ref={containerRef}
      className={`fixed inset-0 z-50 flex justify-center ${variant === "center" ? "items-center" : "items-end sm:items-center"}`}
      onClick={() => onClose()}
    >
      <div ref={backdropRef} className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={`overflow-y-auto outline-none ${
          variant === "center"
            ? "glass w-[calc(100%-2.5rem)] max-w-sm rounded-3xl animate-scale-in max-h-[85dvh] pt-5 pb-6 px-6"
            : variant === "full"
            ? "bg-canvas w-full h-[100dvh] max-h-none rounded-none animate-slide-up pt-safe pb-safe px-6"
            : "glass w-full rounded-t-3xl animate-slide-up max-h-[90dvh] pt-4 pb-safe px-6 sm:max-w-md sm:m-4 sm:rounded-3xl sm:animate-scale-in sm:max-h-[85dvh]"
        } ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Grabber row with an explicit ✕ — a sheet tall enough to hide the
            backdrop gives no hint of how to leave; the close button says it
            (owner UX callout). The spacer keeps the handle optically centred. */}
        {variant !== "center" && (
          <div className="flex items-center justify-between min-h-[2.75rem] -mt-1">
            <span className="w-11" aria-hidden="true" />
            {/* full-screen covers are pages, not sheets — no grabber pill */}
            <div className={`h-1 w-10 bg-surface-strong rounded-full ${variant === "full" ? "hidden" : "sm:hidden"}`} />
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.dismiss")}
              className="w-11 h-11 -mr-3 rounded-full flex items-center justify-center text-fg-muted press"
            >
              ✕
            </button>
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
